
/** Generates a self-contained single-file HTML export of a Guide. */
import { isSameImage } from "./photoCredit";

// A new or emptied guide's page holds placeholder text, not content
// (2026-10-23): the current wording, or any older "The current guide format
// is ..." version still saved in guides (Custom / User Content / Edit Home
// Content). Shared by the export and Build My Trip, on web and mobile alike,
// since a guide saved on one is opened on the other.
export const EMPTY_PAGE_TEXT = "Nothing here yet. Add destinations to build your trip, or tap Edit to write this page yourself.";
export const isPlaceholderDoc = (doc) => {
  const d = String(doc || "").trim();
  return d === "" || d === "<!-- Comment -->" || d.startsWith(EMPTY_PAGE_TEXT) || d.startsWith("The current guide format is");
};


const STATIC_MAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_STATIC_API;
const LABEL_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// Fix internal app link schemes so they work as plain HTML
function sanitizeHTML(html) {
  if (!html) return "";

  // ── Media links: replace entire <a> tag with an embed ──────────────────────

  // videolink → <video> or YouTube/Drive iframe
  html = html.replace(/<a\b[^>]*href="videolink:([^"]*)"[^>]*>(.*?)<\/a>/gis, (_, raw, label) => {
    const url = raw.startsWith("gdrive:")
      ? `https://drive.google.com/file/d/${raw.slice(7)}/preview`
      : raw;
    if (url.includes("youtu")) {
      const vid = url.split("/").pop().replace("watch?v=", "");
      return `<iframe style="max-width:100%;width:560px;height:315px;" src="https://www.youtube.com/embed/${vid}" frameborder="0" allowfullscreen></iframe>`;
    }
    if (raw.startsWith("gdrive:"))
      return `<iframe style="max-width:100%;width:560px;height:315px;" src="${url}" frameborder="0" allowfullscreen></iframe>`;
    return `<video controls style="max-width:100%;width:560px;"><source src="${url}"></video>`;
  });

  // audiolink → <audio> or Drive iframe
  html = html.replace(/<a\b[^>]*href="audiolink:([^"]*)"[^>]*>(.*?)<\/a>/gis, (_, raw, label) => {
    if (raw.startsWith("gdrive:"))
      return `<iframe style="max-width:100%;width:400px;height:100px;" src="https://drive.google.com/file/d/${raw.slice(7)}/preview" frameborder="0"></iframe>`;
    return `<audio controls style="max-width:100%;width:400px;"><source src="${raw}"></audio>`;
  });

  // imagelink → <img>
  html = html.replace(/<a\b[^>]*href="imagelink:([^"]*)"[^>]*>(.*?)<\/a>/gis, (_, raw, label) => {
    const src = raw.startsWith("gdrive:")
      ? `https://lh3.googleusercontent.com/d/${raw.slice(7)}`
      : raw;
    return `<img src="${src}" alt="${label || "Image"}" style="max-width:100%;border-radius:8px;">`;
  });

  // pdflink → regular link
  html = html.replace(/<a\b[^>]*href="pdflink:([^"]*)"[^>]*>(.*?)<\/a>/gis, (_, raw, label) => {
    const href = raw.startsWith("gdrive:")
      ? `https://drive.google.com/file/d/${raw.slice(7)}/view`
      : raw;
    return `<a href="${href}" target="_blank" rel="noopener">${label || "PDF"}</a>`;
  });

  // ── Simple href replacements ────────────────────────────────────────────────

  // urllink → real external link
  html = html.replace(/href="urllink:([^"]*)"/g, 'href="$1" target="_blank" rel="noopener"');

  // App-internal links that have no standalone meaning → neutralise
  const internal = ["maplink", "placemenulink", "placelink", "placemoreinfolink",
                    "placeslink", "sectionlink", "guidelink", "schedulelink",
                    "itinerarylink", "streetviewlink", "directionslink", "weatherlink"];
  internal.forEach(scheme => {
    html = html.replace(new RegExp(`href="${scheme}:[^"]*"`, "g"), 'href="#"');
  });

  html = html.replace(/onerror="window\.imgFallback\(this\)"/g, "");
  return html;
}

/**
 * Builds a Google Static Maps URL for an array of Place objects.
 * Returns "" if there are no mappable places.
 */
// Google Maps Static API max size per dimension is 640px for standard accounts.
export function buildStaticMapUrl(places, width = 600, height = 350) {
  const pins = places.filter(p => p.PType === "Icon" && p.Lat && p.Longi);
  if (pins.length === 0) return "";
  const w = Math.min(width, 640);
  const h = Math.min(height, 640);
  const markers = pins
    .map((p, i) =>
      `&markers=size:mid%7Ccolor:red%7Clabel:${LABEL_CHARS[i % LABEL_CHARS.length]}%7C${p.Lat},${p.Longi}`
    )
    .join("");
  return `https://maps.googleapis.com/maps/api/staticmap?size=${w}x${h}&scale=2&maptype=roadmap${markers}&key=${STATIC_MAP_KEY}`;
}

function buildPlaceLabelMap(places) {
  const labelMap = {};
  const pins = places.filter(p => p.PType === "Icon" && p.Lat && p.Longi);
  pins.forEach((p, i) => { labelMap[String(p.ID)] = LABEL_CHARS[i % LABEL_CHARS.length]; });
  return labelMap;
}

function staticMapImg(url, alt = "Map") {
  if (!url) return "";
  return `<div class="static-map-wrap">
  <img class="static-map" src="${url}" alt="${escHtml(alt)}" referrerpolicy="no-referrer" loading="eager">
</div>`;
}

function escHtml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function resolveImg(url) {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("gdrive:")) return `https://lh3.googleusercontent.com/d/${url.slice(7)}`;
  if (url.includes("http")) return url;
  return `http://liquidguides.com//Media/Photos/${url}`;
}

export function formatDate(dt) {
  try {
    // Use UTC components directly — event times are stored as UTC wall-clock times
    if (typeof dt.toFormat === "function")
      return dt.toFormat("cccc, LLLL d, yyyy");
    const d = new Date(dt);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
      .toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return String(dt); }
}

function formatTime(dt) {
  try {
    if (typeof dt.toFormat === "function")
      return dt.toFormat("h:mm a");
    const d = new Date(dt);
    const h = d.getUTCHours(), m = d.getUTCMinutes();
    const ampm = h >= 12 ? "PM" : "AM";
    return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
  } catch { return ""; }
}

export function dayKey(dt) {
  try {
    if (typeof dt.toISODate === "function") return dt.toISODate();
    return new Date(dt).toISOString().slice(0, 10);
  } catch { return String(dt); }
}

function placeDataFlags(p) {
  const f = [];
  if (p.MustSee)  f.push("mustsee");
  if (p.Favorite) f.push("favorite");
  if (p.Visited)  f.push("visited");
  else            f.push("notvisited");
  return f.join(" ");
}

function renderLinks(hlink) {
  if (!hlink) return "";
  const links = hlink.split(";").map(h => h.trim()).filter(Boolean).map(h => {
    const parts = h.split("|");
    return parts.length >= 2
      ? { label: parts[0], url: parts[1] }
      : h.startsWith("http") ? { label: "Link", url: h } : null;
  }).filter(Boolean);
  if (!links.length) return "";
  return `<div class="place-links">${links.map(l =>
    `<a href="${escHtml(l.url)}" target="_blank" rel="noopener" class="ext-link">${escHtml(l.label)}</a>`
  ).join("")}</div>`;
}

function renderPlaceCard(p, catMap, grpMap) {
  const photoRaw = p.Photo_m || p.Photo;
  const img    = resolveImg(photoRaw);
  const infoText = p.Info || "";
  // The notes already show this photo — any size or address of it, "&amp;"
  // and all (photoCredit.js sameImageKey, 2026-10-18) — so don't add it again.
  const infoSrcs = [...infoText.matchAll(/<img[^>]*\bsrc=["']([^"']+)["']/gi)].map((x) => x[1].replace(/&amp;/g, "&"));
  const infoHasDuplicate = img && (infoText.includes(img) || infoSrcs.some((src) => isSameImage(src, photoRaw) || isSameImage(src, p.Photo)));
  const cats   = (Array.isArray(p.Category) ? p.Category : []).map(id => catMap[String(id)]).filter(Boolean);
  const grps   = (Array.isArray(p.Group)    ? p.Group    : []).map(id => grpMap[String(id)]).filter(Boolean);
  const mapUrl = (p.Lat && p.Longi)
    ? `https://www.google.com/maps/search/?api=1&query=${p.Lat},${p.Longi}` : "";
  const flags  = [p.MustSee && "⭐ Must See", p.Favorite && "♥ Favorite", p.Visited && "✓ Visited"].filter(Boolean);
  const addr   = [p.Address, p.City, p.State, p.Country].filter(Boolean).join(", ");

  return `<div class="place-card" data-flags="${placeDataFlags(p)}" data-name="${escHtml((p.Name || "").toLowerCase())}">
  ${img && !infoHasDuplicate ? `<img class="place-img" src="${img}" referrerpolicy="no-referrer" alt="${escHtml(p.Name)}" loading="lazy" onerror="this.style.display='none'">` : ""}
  <div class="place-body">
    <div class="place-header">
      <h3 class="place-name">${escHtml(p.Name)}</h3>
      ${flags.length ? `<div class="place-flags">${flags.map(f => `<span class="flag">${f}</span>`).join("")}</div>` : ""}
    </div>
    ${(cats.length || grps.length) ? `<div class="place-tags">
      ${cats.map(c => `<span class="tag tag-cat">${escHtml(c)}</span>`).join("")}
      ${grps.map(g => `<span class="tag tag-grp">${escHtml(g)}</span>`).join("")}
    </div>` : ""}
    ${addr ? `<p class="place-address">${escHtml(addr)}${mapUrl ? ` <a href="${mapUrl}" target="_blank" rel="noopener" class="map-link">📍 Map</a>` : ""}</p>` :
      mapUrl ? `<p class="place-address"><a href="${mapUrl}" target="_blank" rel="noopener" class="map-link">📍 View on Map</a></p>` : ""}
    ${p.Duration ? `<p class="place-meta">⏱ ${p.Duration} min</p>` : ""}
    ${p.Phone ? `<p class="place-meta">📞 <a href="tel:${escHtml(p.Phone)}">${escHtml(p.Phone)}</a></p>` : ""}
    ${renderLinks(p.Hlink)}
    ${p.Info ? `<div class="place-info">${sanitizeHTML(p.Info)}</div>` : ""}
  </div>
</div>`;
}

function renderPlacePill(p, catMap, grpMap, labelMap) {
  const label = labelMap ? (labelMap[String(p.ID)] || null) : null;
  const img   = resolveImg(p.Photo_m || p.Photo);
  const loc   = [p.City, p.Country].filter(Boolean).join(", ");
  const addr  = p.Address || "";
  const cats  = (Array.isArray(p.Category) ? p.Category : (p.Category ? [p.Category] : []))
    .map(id => catMap[String(id)]).filter(Boolean);
  const grps  = (Array.isArray(p.Group) ? p.Group : (p.Group ? [p.Group] : []))
    .map(id => grpMap[String(id)]).filter(Boolean);
  const tags  = [...cats, ...grps].slice(0, 4);

  const pillLabel = label ? `<span class="event-place-label">${escHtml(label)}</span>` : "";

  const popup = `<div class="place-popup" role="tooltip">
    ${img ? `<img class="place-popup-img" src="${img}" alt="${escHtml(p.Name)}" loading="eager" referrerpolicy="no-referrer" onerror="this.style.display='none'">` : ""}
    <div class="place-popup-body">
      <div class="place-popup-name">${label ? `<span class="popup-label-badge">${escHtml(label)}</span> ` : ""}${escHtml(p.Name)}</div>
      ${loc  ? `<div class="place-popup-loc">${escHtml(loc)}</div>`  : ""}
      ${addr ? `<div class="place-popup-loc">${escHtml(addr)}</div>` : ""}
      ${tags.length ? `<div class="place-popup-tags">${tags.map(t => `<span class="place-popup-tag">${escHtml(t)}</span>`).join("")}</div>` : ""}
    </div>
  </div>`;

  return `<details class="event-place-wrap">
    <summary class="event-place">${pillLabel}${escHtml(p.Name)}</summary>
    ${popup}
  </details>`;
}

function renderSchedule(guide, selectedScheduleNames = null, catMap = {}, grpMap = {}) {
  const events = guide.schedule.events;
  if (!events || events.length === 0) return "";

  // Group by itinerary name first, preserving insertion order
  const itineraryMap = new Map();
  events.forEach(ev => {
    const name = (ev.Itinerary || "Main").replace(/%20/g, " ");
    if (selectedScheduleNames && !selectedScheduleNames.includes(name)) return;
    if (!itineraryMap.has(name)) itineraryMap.set(name, []);
    itineraryMap.get(name).push(ev);
  });

  const multipleSchedules = itineraryMap.size > 1;

  let html = `<section id="schedule"><h2 class="section-title">Schedule</h2>`;

  itineraryMap.forEach((schedEvents, itineraryName) => {
    if (multipleSchedules)
      html += `<div class="schedule-block"><h3 class="schedule-name">${escHtml(itineraryName)}</h3>`;

    const dayMap = new Map();
    schedEvents.forEach(ev => {
      const key = dayKey(ev.Start);
      if (!dayMap.has(key)) dayMap.set(key, []);
      dayMap.get(key).push(ev);
    });

    dayMap.forEach(dayEvents => {
      const dayPlaceIds = new Set();
      dayEvents.forEach(ev => (ev.Places || "").split(",").forEach(id => { if (id.trim()) dayPlaceIds.add(id.trim()); }));
      const dayPlaces = [...dayPlaceIds].map(id => guide.places.getPlace(id)).filter(Boolean);
      const dayMapUrl = buildStaticMapUrl(dayPlaces, 640, 300);
      const dayLabelMap = buildPlaceLabelMap(dayPlaces);

      html += `<div class="day-block">
        <h3 class="day-heading">${escHtml(formatDate(dayEvents[0].Start))}</h3>
        ${staticMapImg(dayMapUrl, formatDate(dayEvents[0].Start))}`;

      dayEvents.forEach(ev => {
        const evPlaces = (ev.Places || "").split(",").map(id => guide.places.getPlace(id.trim())).filter(Boolean);
        const pillsHTML = evPlaces.map(p => renderPlacePill(p, catMap, grpMap, dayLabelMap)).join("");

        html += `<div class="event-card">
          <div class="event-time">${escHtml(formatTime(ev.Start))}${ev.End ? " – " + escHtml(formatTime(ev.End)) : ""}</div>
          <strong class="event-subject">${escHtml(ev.Subject)}</strong>
          ${pillsHTML ? `<div class="event-places">${pillsHTML}</div>` : ""}
          ${ev.Body ? `<div class="event-body">${sanitizeHTML(ev.Body)}</div>` : ""}
        </div>`;
      });
      html += `</div>`;
    });

    if (multipleSchedules) html += `</div>`;
  });

  html += `</section>`;
  return html;
}

function renderChapters(chapters) {
  if (!chapters || chapters.length === 0) return "";
  let html = `<section id="chapters"><h2 class="section-title">My Notes</h2>`;
  chapters.forEach(ch => {
    const secs = (ch.sections || []).filter(s => s.html);
    if (!secs.length) return;
    html += `<div class="chapter">
      <h3 class="chapter-title">${escHtml(ch.Title || "")}</h3>
      ${secs.map(s => `<div class="section-html">${sanitizeHTML(s.html)}</div>`).join("")}
    </div>`;
  });
  html += `</section>`;
  return html;
}

const SECTION_ORDER_DEFAULT = ["itinerary", "schedule", "places", "chapters"];

/**
 * @param {object} guide
 * @param {object} [options]
 * @param {boolean} [options.includeHero=true]
 * @param {boolean} [options.includeStats=true]
 * @param {boolean} [options.includeOverallMap=true]
 * @param {Array<{id:string,enabled:boolean}>} [options.sections]
 */
export function generateTripHTML(guide, options = {}) {
  const {
    includeHero           = true,
    includeStats          = true,
    includeOverallMap     = true,
    sections: sectionOpts = SECTION_ORDER_DEFAULT.map(id => ({ id, enabled: true })),
    selectedScheduleNames = null,
  } = options;

  const attr   = guide.attributes || {};
  const places = (guide.places.placesList || []).filter(p => !p.Hidden && p.PType !== "PolyLine" && p.PType !== "Polygon");
  const catMap = Object.fromEntries((guide.categories || []).map(c => [String(c.id), c.name]));
  const grpMap = Object.fromEntries((guide.groups    || []).map(g => [String(g.id), g.name]));

  const coverImg     = resolveImg(attr.image_m || attr.image);
  const tripName     = attr.guide_name || "Trip";
  const description  = attr.description || "";
  const countries    = Array.isArray(attr.countries) ? attr.countries.join(", ") : (attr.countries || "");
  const cities       = Array.isArray(attr.cities)    ? attr.cities.join(", ")    : (attr.cities    || "");
  const visitedCount = places.filter(p => p.Visited).length;
  const mustSeeCount = places.filter(p => p.MustSee).length;
  const favCount     = places.filter(p => p.Favorite).length;

  const placeCardsHTML = places.map(p => renderPlaceCard(p, catMap, grpMap)).join("\n");
  const scheduleHTML   = renderSchedule(guide, selectedScheduleNames, catMap, grpMap);
  const chaptersHTML   = renderChapters(guide.chapters || []);

  const hasSched    = Array.isArray(guide.schedule.events) && guide.schedule.events.length > 0;
  const hasChapters = (guide.chapters || []).some(ch => (ch.sections || []).some(s => s.html));

  const tripMapUrl = buildStaticMapUrl(places, 640, 400);

  // (sanitizeHTML is defined at module level)

  const rawDoc = guide.document || "";
  const hasDoc = !isPlaceholderDoc(rawDoc);
  const itineraryHTML = hasDoc ? sanitizeHTML(rawDoc) : "";

  // Determine which sections to include and in what order
  const sectionEnabled = Object.fromEntries(sectionOpts.map(s => [s.id, s.enabled]));

  const sectionRenderers = {
    itinerary: () => (itineraryHTML && sectionEnabled.itinerary)
      ? `<section id="itinerary">
    <h2 class="section-title">Itinerary</h2>
    <div class="itinerary-body">${itineraryHTML}</div>
  </section>` : "",
    places: () => (places.length && sectionEnabled.places)
      ? `<section id="places">
    <h2 class="section-title">Places</h2>
    <div class="filter-bar">
      <input type="text" id="search" placeholder="Search places…" oninput="applyFilters()">
      <button class="filter-btn active" data-filter="all"       onclick="setFilter(this)">All</button>
      ${mustSeeCount ? `<button class="filter-btn" data-filter="mustsee"   onclick="setFilter(this)">⭐ Must See</button>` : ""}
      ${favCount     ? `<button class="filter-btn" data-filter="favorite"  onclick="setFilter(this)">♥ Favorites</button>` : ""}
      ${visitedCount ? `<button class="filter-btn" data-filter="visited"   onclick="setFilter(this)">✓ Visited</button>` : ""}
      ${places.length - visitedCount > 0 ? `<button class="filter-btn" data-filter="notvisited" onclick="setFilter(this)">Not Visited</button>` : ""}
    </div>
    <div id="places-grid">
      ${placeCardsHTML}
    </div>
    <p id="no-results">No places match your filter.</p>
  </section>` : "",
    schedule: () => (hasSched && sectionEnabled.schedule) ? scheduleHTML : "",
    chapters: () => (hasChapters && sectionEnabled.chapters) ? chaptersHTML : "",
  };

  const orderedSectionsHTML = sectionOpts
    .map(s => (sectionRenderers[s.id] ? sectionRenderers[s.id]() : ""))
    .join("\n");

  // TOC links in the user-chosen order
  const tocLinks = sectionOpts
    .filter(s => s.enabled)
    .map(s => {
      if (s.id === "itinerary" && itineraryHTML) return `<li><a href="#itinerary">Itinerary</a></li>`;
      if (s.id === "places"    && places.length) return `<li><a href="#places">Places (${places.length})</a></li>`;
      if (s.id === "schedule"  && hasSched)      return `<li><a href="#schedule">Schedule</a></li>`;
      if (s.id === "chapters"  && hasChapters)   return `<li><a href="#chapters">My Notes</a></li>`;
      return "";
    })
    .join("\n      ");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escHtml(tripName)}</title>
<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
       background: #f5f6f8; color: #1a1a2e; line-height: 1.5; }
a { color: #3b82f6; }

/* ── Hero ── */
.hero {
  position: relative; min-height: 280px;
  background: #1a1a2e;
  display: flex; align-items: flex-end;
  overflow: hidden;
}
.hero-img {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; object-position: center;
  opacity: 0.55;
}
.hero-content {
  position: relative; z-index: 1;
  padding: 24px 32px; color: #fff; width: 100%;
}
.hero-title { font-size: clamp(1.8rem, 5vw, 3rem); font-weight: 700; line-height: 1.15; }
.hero-desc  { font-size: 1rem; opacity: 0.85; margin-top: 8px; max-width: 680px; }
.hero-meta  { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 14px; font-size: 0.85rem; opacity: 0.8; }
.hero-meta span::before { margin-right: 4px; }

/* ── Stats bar ── */
.stats-bar {
  background: #fff; border-bottom: 1px solid #e5e7eb;
  display: flex; flex-wrap: wrap; gap: 0;
}
.stat {
  flex: 1 1 120px; text-align: center;
  padding: 14px 8px; border-right: 1px solid #e5e7eb;
}
.stat:last-child { border-right: none; }
.stat-val { font-size: 1.5rem; font-weight: 700; color: #3b82f6; }
.stat-lbl { font-size: 0.75rem; color: #6b7280; margin-top: 2px; }

/* ── Layout ── */
.container { max-width: 1100px; margin: 0 auto; padding: 0 16px; }

/* ── TOC ── */
.toc {
  background: #fff; border-radius: 10px; padding: 20px 24px;
  margin: 24px 0; border: 1px solid #e5e7eb;
}
.toc h2 { font-size: 0.85rem; text-transform: uppercase; letter-spacing: .06em;
           color: #9ca3af; margin-bottom: 10px; }
.toc ul { list-style: none; display: flex; flex-wrap: wrap; gap: 8px; }
.toc a  { background: #f3f4f6; border-radius: 20px; padding: 6px 14px;
           text-decoration: none; color: #374151; font-size: 0.9rem;
           border: 1px solid #e5e7eb; }
.toc a:hover { background: #3b82f6; color: #fff; border-color: #3b82f6; }

/* ── Filter toolbar ── */
.filter-bar {
  background: #fff; border-radius: 10px; padding: 12px 16px;
  margin-bottom: 18px; border: 1px solid #e5e7eb;
  display: flex; flex-wrap: wrap; align-items: center; gap: 8px;
}
.filter-bar input {
  flex: 1 1 200px; border: 1px solid #e5e7eb; border-radius: 20px;
  padding: 6px 14px; font-size: 0.9rem; outline: none;
  background: #f9fafb;
}
.filter-bar input:focus { border-color: #3b82f6; background: #fff; }
.filter-btn {
  padding: 6px 14px; border-radius: 20px; border: 1px solid #e5e7eb;
  background: #f3f4f6; color: #374151; font-size: 0.85rem; cursor: pointer;
}
.filter-btn.active { background: #3b82f6; color: #fff; border-color: #3b82f6; }

/* ── Section title ── */
.section-title {
  font-size: 1.5rem; font-weight: 700; color: #1a1a2e;
  padding-bottom: 10px; margin-bottom: 20px;
  border-bottom: 3px solid #3b82f6; display: inline-block;
}
section { padding: 32px 0; }

/* ── Place grid ── */
#places-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}
.place-card {
  background: #fff; border-radius: 12px; overflow: hidden;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0,0,0,.07);
  display: flex; flex-direction: column;
  transition: box-shadow .15s;
  /* Grid gap on #places-grid is pure layout spacing — it contributes
     nothing to a selection's copied plain text, since it's not a real DOM
     node or CSS margin. Selecting from one card's trailing content (e.g. a
     Wikipedia "...See more" link) straight into the next card's name ran
     them together with no space when copied, even though they look
     separated on screen. A real margin, unlike gap, is respected by
     browsers' text-selection serialization. Found 2026-10-05. */
  margin-bottom: 20px;
}
.place-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,.12); }
.place-card.hidden-card { display: none; }
.place-img {
  width: 100%; height: 200px;
  object-fit: cover; object-position: center;
  display: block;
}
.place-body { padding: 14px 16px 16px; display: flex; flex-direction: column; gap: 8px; }
.place-header { display: flex; flex-direction: column; gap: 4px; }
.place-name { font-size: 1.05rem; font-weight: 700; color: #1a1a2e; }
.place-flags { display: flex; flex-wrap: wrap; gap: 4px; }
.flag {
  font-size: 0.72rem; padding: 2px 8px; border-radius: 10px;
  background: #fef3c7; color: #92400e; font-weight: 600;
}
.place-tags { display: flex; flex-wrap: wrap; gap: 4px; }
.tag {
  font-size: 0.72rem; padding: 2px 8px; border-radius: 10px;
  font-weight: 500;
}
.tag-cat { background: #ede9fe; color: #5b21b6; }
.tag-grp { background: #d1fae5; color: #065f46; }
.place-address { font-size: 0.85rem; color: #6b7280; }
.map-link { font-size: 0.8rem; margin-left: 4px; white-space: nowrap; }
.place-meta { font-size: 0.82rem; color: #6b7280; }
.place-links { display: flex; flex-wrap: wrap; gap: 6px; }
.ext-link {
  font-size: 0.8rem; padding: 3px 10px; border-radius: 6px;
  background: #f0f9ff; border: 1px solid #bae6fd; color: #0369a1;
  text-decoration: none;
}
.ext-link:hover { background: #e0f2fe; }
.place-info { font-size: 0.88rem; color: #374151; margin-top: 4px;
              border-top: 1px solid #f3f4f6; padding-top: 8px; }
.place-info img { max-width: 100%; height: auto; border-radius: 6px; }

/* ── Schedule ── */
.schedule-block { margin-bottom: 40px; }
.schedule-name {
  font-size: 1.25rem; font-weight: 700; color: #fff;
  background: #1e40af; padding: 10px 18px; border-radius: 8px; margin-bottom: 16px;
}
.day-block { margin-bottom: 32px; }
.day-heading {
  font-size: 1.1rem; font-weight: 700; color: #1a1a2e;
  background: #f0f9ff; border-left: 4px solid #3b82f6;
  padding: 8px 14px; border-radius: 0 8px 8px 0; margin-bottom: 12px;
}
.event-card {
  background: #fff; border-radius: 10px; padding: 14px 16px;
  border: 1px solid #e5e7eb; margin-bottom: 10px;
  display: flex; flex-direction: column; gap: 6px;
}
.event-time { font-size: 0.8rem; color: #9ca3af; font-weight: 600; text-transform: uppercase; }
.event-subject { font-size: 1rem; font-weight: 700; }
.event-places { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 2px; }
/* details used as popup container — keep it inline like a span */
.event-place-wrap { position: relative; display: inline-block; }
.event-place-wrap summary { list-style: none; }
.event-place-wrap summary::-webkit-details-marker { display: none; }
.event-place {
  font-size: 0.78rem; padding: 2px 10px; border-radius: 10px;
  background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb;
  cursor: pointer; font-family: inherit;
  transition: background 0.15s, border-color 0.15s;
}
.event-place:hover { background: #dbeafe; border-color: #93c5fd; color: #1e40af; }
.event-place-wrap[open] > summary.event-place { background: #dbeafe; border-color: #3b82f6; color: #1e40af; }
.event-place-label {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: #ef4444; color: #fff; font-size: 0.65rem; font-weight: 700;
  margin-right: 5px; vertical-align: middle; flex-shrink: 0;
}
.popup-label-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 50%;
  background: #ef4444; color: #fff; font-size: 0.7rem; font-weight: 700;
  margin-right: 5px; vertical-align: middle;
}
.place-popup {
  position: absolute; bottom: calc(100% + 6px); left: 0;
  z-index: 100; width: 220px; background: #fff;
  border: 1px solid #e5e7eb; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.13); overflow: hidden;
}
.place-popup-img { width: 100%; height: 120px; object-fit: cover; display: block; }
.place-popup-body { padding: 8px 10px; }
.place-popup-name { font-weight: 600; font-size: 0.82rem; color: #1a1a2e; margin-bottom: 3px; }
.place-popup-loc  { font-size: 0.75rem; color: #6b7280; margin-bottom: 4px; }
.place-popup-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.place-popup-tag  { font-size: 0.68rem; padding: 1px 7px; border-radius: 8px;
                    background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.event-body { font-size: 0.88rem; color: #374151; margin-top: 4px; }
.event-body img { max-width: 100%; height: auto; border-radius: 6px; }

/* ── Chapters ── */
.chapter { margin-bottom: 32px; }
.chapter-title {
  font-size: 1.15rem; font-weight: 700; margin-bottom: 14px; color: #1a1a2e;
  padding-bottom: 6px; border-bottom: 2px solid #e5e7eb;
}
.section-html { font-size: 0.9rem; color: #374151; margin-bottom: 16px; }
.section-html img { max-width: 100%; height: auto; border-radius: 8px; }
.section-html h1,.section-html h2,.section-html h3 { margin: .6em 0 .3em; font-weight: 700; }

/* ── Itinerary ── */
.itinerary-body { font-size: 0.92rem; color: #1a1a2e; line-height: 1.65; }
.itinerary-body img { max-width: 100%; height: auto; border-radius: 8px; }
.itinerary-body h1,.itinerary-body h2 { font-size: 1.25rem; font-weight: 700; margin: .8em 0 .3em; }
.itinerary-body h3 { font-size: 1.05rem; font-weight: 700; margin: .6em 0 .25em; }
.itinerary-body ol,.itinerary-body ul { padding-left: 1.4em; margin: .4em 0; }
.itinerary-body a[href="#"] { color: inherit; text-decoration: none; pointer-events: none; }

/* ── Static maps ── */
.static-map-wrap {
  width: 100%; border-radius: 10px; overflow: hidden;
  margin: 16px 0; border: 1px solid #e5e7eb;
  line-height: 0;
}
.static-map {
  width: 100%; height: auto; display: block;
}

/* ── No-results ── */
#no-results {
  text-align: center; padding: 40px 16px; color: #9ca3af;
  font-size: 1rem; display: none;
}

/* ── Footer ── */
footer {
  text-align: center; padding: 24px 16px; font-size: 0.78rem;
  color: #9ca3af; border-top: 1px solid #e5e7eb; margin-top: 40px;
}

@media (max-width: 600px) {
  .hero-content { padding: 16px; }
  #places-grid { grid-template-columns: 1fr; }
}

/* ── Print / PDF ── */
@media print {
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { background: #fff; font-size: 10pt; }

  /* hide interactive chrome */
  .filter-bar, #no-results, .toc a { display: none !important; }
  .toc { border: none; padding: 0; margin: 12pt 0; }
  .toc ul { gap: 0; }
  .toc li { display: inline; }
  .toc li::after { content: " · "; color: #9ca3af; }
  .toc li:last-child::after { content: ""; }
  .toc a { display: inline !important; background: none; border: none;
            padding: 0; color: #1a1a2e; font-size: 9pt; }

  /* hero: keep background colour, make shorter */
  .hero { min-height: 160pt; page-break-after: avoid; }
  .hero-title { font-size: 22pt; }
  .hero-desc  { font-size: 10pt; }

  /* stats bar */
  .stats-bar { border: 1pt solid #e5e7eb; margin-bottom: 6pt; }
  .stat-val  { font-size: 16pt; }

  /* section breaks */
  section { padding: 12pt 0; page-break-before: always; }
  section:first-of-type { page-break-before: avoid; }
  .section-title { font-size: 16pt; margin-bottom: 10pt; }

  /* place grid → 2-column */
  #places-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10pt;
  }
  .place-card {
    break-inside: avoid;
    page-break-inside: avoid;
    box-shadow: none;
    border: 1pt solid #d1d5db;
  }
  .place-img { height: 120pt; }
  .place-body { padding: 8pt 10pt; gap: 5pt; }
  .place-name { font-size: 11pt; }
  .place-info { font-size: 8pt; }

  /* schedule */
  .day-block { page-break-inside: avoid; margin-bottom: 14pt; }
  .day-heading { font-size: 11pt; padding: 5pt 10pt; }
  .event-card  { break-inside: avoid; page-break-inside: avoid;
                 border: 1pt solid #e5e7eb; padding: 8pt 10pt; margin-bottom: 6pt; }

  /* place pills: expand inline for print/PDF */
  .event-place-wrap { display: inline-block; vertical-align: top; margin: 2pt 4pt 2pt 0; }
  .event-place { cursor: default; }
  .event-place-wrap .place-popup {
    display: block !important; position: static !important;
    width: auto; box-shadow: none;
    border: 1pt solid #e5e7eb; border-radius: 6pt;
    overflow: hidden; margin-top: 3pt;
    break-inside: avoid;
  }
  .place-popup-img { height: 80pt; width: 100%; object-fit: cover; }
  .place-popup-body { padding: 4pt 6pt; }
  .place-popup-name { font-size: 8pt; font-weight: 700; }
  .place-popup-loc  { font-size: 7pt; }
  .place-popup-tag  { font-size: 6pt; }

  /* itinerary */
  .itinerary-body img { max-width: 100%; }
  #itinerary { page-break-before: always; }

  /* static maps */
  .static-map-wrap { border: 1pt solid #d1d5db; border-radius: 6pt; margin: 8pt 0; }
  .day-block .static-map-wrap { margin-bottom: 8pt; }

  /* chapters */
  .chapter { page-break-inside: avoid; }
  .chapter-title { font-size: 12pt; }

  /* footer */
  footer { font-size: 8pt; border-top: 1pt solid #e5e7eb; padding: 8pt; }

  /* links: show URL after text */
  a[href^="http"]::after { content: " (" attr(href) ")"; font-size: 7pt; color: #6b7280; }
  .map-link::after { content: none; }
}
</style>
</head>
<body>

${includeHero ? `<!-- Hero -->
<header class="hero">
  ${coverImg ? `<img class="hero-img" src="${coverImg}" referrerpolicy="no-referrer" alt="${escHtml(tripName)}" onerror="this.remove()">` : ""}
  <div class="hero-content">
    <h1 class="hero-title">${escHtml(tripName)}</h1>
    ${description ? `<p class="hero-desc">${escHtml(description)}</p>` : ""}
    <div class="hero-meta">
      ${countries ? `<span>🌍 ${escHtml(countries)}</span>` : ""}
      ${cities    ? `<span>🏙 ${escHtml(cities)}</span>`    : ""}
    </div>
  </div>
</header>` : ""}

${includeStats ? `<!-- Stats -->
<div class="stats-bar">
  <div class="stat"><div class="stat-val">${places.length}</div><div class="stat-lbl">Places</div></div>
  ${mustSeeCount ? `<div class="stat"><div class="stat-val">${mustSeeCount}</div><div class="stat-lbl">Must See</div></div>`  : ""}
  ${visitedCount ? `<div class="stat"><div class="stat-val">${visitedCount}</div><div class="stat-lbl">Visited</div></div>`  : ""}
  ${favCount     ? `<div class="stat"><div class="stat-val">${favCount}</div><div class="stat-lbl">Favorites</div></div>`    : ""}
  ${hasSched     ? `<div class="stat"><div class="stat-val">${(guide.schedule.events || []).length}</div><div class="stat-lbl">Events</div></div>` : ""}
</div>` : ""}

<div class="container">

  ${(includeOverallMap && tripMapUrl) ? `<!-- Overall trip map -->
  ${staticMapImg(tripMapUrl, tripName)}` : ""}

  ${tocLinks ? `<!-- TOC -->
  <nav class="toc">
    <h2>Contents</h2>
    <ul>
      ${tocLinks}
    </ul>
  </nav>` : ""}

  ${orderedSectionsHTML}

</div>

<footer>
  Exported from Andiamo Places · ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
</footer>

<script>
document.querySelectorAll(".event-place-wrap").forEach(function(d) {
  d.addEventListener("toggle", function() {
    if (d.open) {
      // iOS Safari doesn't fetch images inside closed <details> even with loading="eager".
      // Force a load by bouncing the src attribute now that the element is visible.
      d.querySelectorAll("img.place-popup-img").forEach(function(img) {
        if (!img.complete || img.naturalWidth === 0) {
          var src = img.src;
          img.src = "";
          img.src = src;
        }
      });
      // Close all other open popups.
      document.querySelectorAll(".event-place-wrap[open]").forEach(function(other) {
        if (other !== d) other.open = false;
      });
    }
  });
});

var activeFilter = "all";

function setFilter(btn) {
  activeFilter = btn.dataset.filter;
  document.querySelectorAll(".filter-btn").forEach(function(b) {
    b.classList.toggle("active", b === btn);
  });
  applyFilters();
}

function applyFilters() {
  var q = (document.getElementById("search").value || "").toLowerCase().trim();
  var cards = document.querySelectorAll(".place-card");
  var visible = 0;
  cards.forEach(function(card) {
    var flags = card.dataset.flags || "";
    var name  = card.dataset.name  || "";
    var passFilter = activeFilter === "all" || flags.indexOf(activeFilter) !== -1;
    var passSearch = !q || name.indexOf(q) !== -1 ||
      (card.textContent || "").toLowerCase().indexOf(q) !== -1;
    var show = passFilter && passSearch;
    card.classList.toggle("hidden-card", !show);
    if (show) visible++;
  });
  var noRes = document.getElementById("no-results");
  if (noRes) noRes.style.display = visible === 0 ? "block" : "none";
}
</script>

</body>
</html>`;
}

/**
 * Prints the trip using a hidden iframe so the full system print dialog
 * (with all installed printers) is shown instead of the popup-only PDF saver.
 */
export function printTripAsPDF(guide, options = {}) {
  let html = generateTripHTML(guide, options);
  html = html.replace(/\bloading="lazy"/g, 'loading="eager"');

  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url  = URL.createObjectURL(blob);

  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;left:-9999px;top:-9999px;width:1200px;height:900px;border:none;visibility:hidden;";
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      URL.revokeObjectURL(url);
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
    }, 3000);
  };

  iframe.onload = () => {
    const win = iframe.contentWindow;
    const doc = iframe.contentDocument;

    const doPrint = () => { win.focus(); win.print(); cleanup(); };

    const imgs    = Array.from(doc.images);
    const pending = imgs.filter(img => !img.complete);

    if (pending.length === 0) { setTimeout(doPrint, 200); return; }

    let settled = 0;
    const timer = setTimeout(doPrint, 12000);
    const onSettle = () => {
      if (++settled >= pending.length) { clearTimeout(timer); doPrint(); }
    };
    pending.forEach(img => {
      img.addEventListener("load",  onSettle, { once: true });
      img.addEventListener("error", onSettle, { once: true });
    });
  };

  iframe.src = url;
}
